import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CreditCard, Spinner, Tag, Info, CheckCircle, MagnifyingGlass, ClipboardText, Wrench, Receipt, Lightning } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { BUSINESS } from "@/data/site";
import PayPalWalletButtons from "@/components/PayPalWalletButtons";

const STANDARD_ID = "diagnostic-standard";
const EMERGENCY_ID = "diagnostic-emergency";

const initial = {
  name: "",
  email: "",
  phone: "",
  preferred_date: "",
  address: "",
  notes: "",
  package_id: STANDARD_ID,
};

const STEPS = [
  { icon: ClipboardText, title: "Schedule your visit", desc: "Pick a preferred date and share what the appliance is doing — we'll confirm the time window." },
  { icon: MagnifyingGlass, title: "On-site inspection", desc: "A licensed technician arrives with the tools to diagnose the appliance and identify the cause of the issue." },
  { icon: Receipt, title: "Written repair quote", desc: "You receive an upfront, written repair quote — no work happens until you approve it." },
  { icon: Wrench, title: "Approve & repair", desc: "If you approve the repair, you only pay the quoted repair price. The diagnostic fee is a separate one-time charge for the technician's on-site visit." },
];

const INCLUDED = [
  "Licensed & insured appliance technician",
  "Full diagnostic inspection of the appliance",
  "Root-cause identification (parts and labor scope)",
  "Written repair quote before any work is done",
  "Honest recommendation — we'll say if it's not worth repairing",
  "Hassle-free. No surprise fees.",
];

const TIERS = [
  {
    id: STANDARD_ID,
    label: "Standard",
    price: 79,
    icon: MagnifyingGlass,
    tag: "Most visits",
    desc: "Standard on-site diagnostic. Under 45 minutes from the technician's location.",
  },
  {
    id: EMERGENCY_ID,
    label: "Emergency / Long-Distance",
    price: 119,
    icon: Lightning,
    tag: "45+ min drive or urgent",
    desc: "Emergency response or jobs over 45 minutes from the technician's location.",
  },
];

const SURCHARGE_PCT = 5;
const computeTotals = (base) => {
  const b = Number(base) || 0;
  const surcharge = Math.round(b * (SURCHARGE_PCT / 100) * 100) / 100;
  const total = Math.round((b + surcharge) * 100) / 100;
  return { base: b, surcharge, total };
};

export default function DiagnosticPage() {
  const [packages, setPackages] = useState([]);
  const [pkgLoading, setPkgLoading] = useState(true);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/payments/packages`);
        const data = await res.json();
        setPackages(data.packages || []);
      } catch { toast.error("Failed to load diagnostic visit info."); }
      finally { setPkgLoading(false); }
    })();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedTier = TIERS.find((t) => t.id === form.package_id) || TIERS[0];
  const selectedPkg = packages.find((p) => p.id === form.package_id);
  const totals = computeTotals(selectedTier.price);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) e.phone = "Valid phone required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/booking", {
        package_id: form.package_id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        preferred_date: form.preferred_date || null,
        address: form.address || null,
        notes: form.notes || null,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else toast.error("Payment link not returned. Please try again.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Booking failed");
    } finally { setSubmitting(false); }
  };

  const createPayPalOrder = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      throw new Error("form invalid");
    }
    const { data } = await api.post("/payments/booking", {
      package_id: form.package_id,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      preferred_date: form.preferred_date || null,
      address: form.address || null,
      notes: form.notes || null,
      origin_url: window.location.origin,
    });
    if (!data?.order_id) throw new Error("No order id");
    return data.order_id;
  };

  return (
    <div data-testid="diagnostic-page">
      {/* Hero */}
      <section className="ji-container pt-16 md:pt-24 pb-10">
        <div className="ji-overline mb-5 inline-flex items-center gap-2">
          <CreditCard size={12} weight="fill" /> Appliance Repair Diagnostic Visit
        </div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight max-w-3xl">
          Broken appliance?<br /><span className="text-[var(--ji-accent)] italic">Let&rsquo;s find out why.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[var(--ji-muted)] leading-relaxed">
          A licensed technician comes to your home, inspects the appliance, and identifies exactly what&rsquo;s wrong.
          You get a written repair quote before any work begins.
        </p>
        <p className="mt-3 max-w-2xl text-sm text-[var(--ji-muted)] leading-relaxed">
          <strong className="text-[var(--ji-text)]">Hassle-free. No surprise fees.</strong>{" "}
          Not an appliance repair? All other services are booked at $79 (or $119 for emergency/long-distance) — credited toward the final job cost.{" "}
          <Link to="/contact" className="text-[var(--ji-primary)] font-semibold hover:text-[var(--ji-accent)] underline">Request a quote →</Link>
        </p>
      </section>

      {/* How it works — 4 steps */}
      <section className="ji-container pb-6" data-testid="diagnostic-how-it-works">
        <div className="ji-overline mb-6">How a diagnostic visit works</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} data-testid={`diagnostic-step-${i}`} className="ji-card p-6 flex flex-col" style={{ borderRadius: 4 }}>
                <div className="flex items-center gap-3">
                  <span className="inline-flex w-10 h-10 items-center justify-center bg-[var(--ji-surface-alt)] text-[var(--ji-primary)]" style={{ borderRadius: 4 }}>
                    <Icon size={20} weight="duotone" />
                  </span>
                  <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold text-[var(--ji-accent)]">0{i + 1}</span>
                </div>
                <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-5 text-lg font-bold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-[var(--ji-muted)] leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="ji-container pb-24 pt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Details form */}
        <div className="lg:col-span-7">
          <div data-testid="diagnostic-form" className="ji-card p-7 md:p-10 space-y-6" style={{ borderRadius: 4 }}>
            <div className="ji-overline inline-flex items-center gap-2"><Tag size={12} weight="fill" /> Choose your visit type</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="diagnostic-tier-picker">
              {TIERS.map((t) => {
                const Icon = t.icon;
                const active = form.package_id === t.id;
                return (
                  <button
                    type="button"
                    key={t.id}
                    data-testid={`diagnostic-tier-${t.id}`}
                    onClick={() => update("package_id", t.id)}
                    className={`text-left p-5 border-2 transition-all ${
                      active
                        ? "border-[var(--ji-accent)] bg-[var(--ji-surface-alt)]"
                        : "border-[var(--ji-border)] hover:border-[var(--ji-muted)]"
                    }`}
                    style={{ borderRadius: 4 }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Icon size={20} weight="duotone" className={active ? "text-[var(--ji-accent)]" : "text-[var(--ji-primary)]"} />
                        <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-lg font-bold">{t.label}</span>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--ji-muted)] whitespace-nowrap">{t.tag}</span>
                    </div>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span style={{ fontFamily: "Cabinet Grotesk" }} className={`text-4xl font-bold ${active ? "text-[var(--ji-accent)]" : "text-[var(--ji-primary)]"}`}>${t.price}</span>
                      <span className="text-xs text-[var(--ji-muted)]">flat</span>
                    </div>
                    <p className="mt-2 text-xs text-[var(--ji-muted)] leading-relaxed">{t.desc}</p>
                  </button>
                );
              })}
            </div>

            <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight pt-2">Schedule your diagnostic.</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="d-name" className="text-sm font-medium">Full name *</Label>
                <Input id="d-name" data-testid="diagnostic-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="h-12 mt-2" placeholder="Jane Doe" />
                {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="d-phone" className="text-sm font-medium">Phone *</Label>
                <Input id="d-phone" data-testid="diagnostic-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="h-12 mt-2" placeholder="(504) 555-0100" />
                {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="d-email" className="text-sm font-medium">Email *</Label>
                <Input id="d-email" type="email" data-testid="diagnostic-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="h-12 mt-2" placeholder="you@email.com" />
                {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="d-date" className="text-sm font-medium">Preferred date</Label>
                <Input id="d-date" type="date" data-testid="diagnostic-date" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} className="h-12 mt-2" />
              </div>
              <div>
                <Label htmlFor="d-address" className="text-sm font-medium">Address</Label>
                <Input id="d-address" data-testid="diagnostic-address" value={form.address} onChange={(e) => update("address", e.target.value)} className="h-12 mt-2" placeholder="Metairie, LA" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="d-notes" className="text-sm font-medium">Appliance details</Label>
                <Textarea id="d-notes" data-testid="diagnostic-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} className="mt-2 min-h-[120px]" placeholder="Brand · model number if known · what's happening (not cooling, leaking, won't spin, error code, etc.)" />
              </div>
            </div>

            <div className="pt-2">
              <PayPalWalletButtons
                amount={totals.total}
                disabled={submitting || pkgLoading}
                createOrder={createPayPalOrder}
                onApproved={(sessionId) => { window.location.href = `/payments/success?session_id=${sessionId}`; }}
              />
              <p className="mt-3 text-xs text-[var(--ji-muted)]">
                Total to charge: <strong className="text-[var(--ji-text)]">${totals.total.toFixed(2)}</strong> · includes 5% card processing surcharge.
              </p>
            </div>
          </div>
        </div>

        {/* Summary sidebar */}
        <aside className="lg:col-span-5 space-y-5 lg:sticky lg:top-24 self-start">
          <motion.div key={form.package_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="ji-card p-8" style={{ borderRadius: 4 }} data-testid="diagnostic-summary">
            <div className="ji-overline mb-4 inline-flex items-center gap-2"><Tag size={12} weight="fill" /> What you&rsquo;re getting</div>
            {pkgLoading ? (
              <div className="text-[var(--ji-muted)] text-sm inline-flex items-center gap-2"><Spinner size={16} className="animate-spin" /> Loading…</div>
            ) : (
              <>
                <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold tracking-tight">
                  {selectedPkg?.label || `Appliance Repair Diagnostic — ${selectedTier.label}`}
                </div>
                <p className="text-sm text-[var(--ji-muted)] mt-2 leading-relaxed">
                  A licensed technician on-site to inspect the appliance, identify the issue, and provide a written repair quote before any work is done.
                </p>
                <div className="mt-6 border-t border-[var(--ji-border)] pt-5 space-y-2">
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-[var(--ji-muted)]">Diagnostic fee</span>
                    <span className="font-semibold" data-testid="diagnostic-base">${totals.base.toFixed(2)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-[var(--ji-muted)]">Card processing surcharge (5%)</span>
                    <span className="font-semibold" data-testid="diagnostic-surcharge">${totals.surcharge.toFixed(2)}</span>
                  </div>
                  <div className="flex items-baseline justify-between pt-2 border-t border-[var(--ji-border)]">
                    <span className="text-sm text-[var(--ji-muted)]">Total due</span>
                    <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl font-bold text-[var(--ji-primary)]" data-testid="diagnostic-price">${totals.total.toFixed(2)}</span>
                  </div>
                </div>
                <div className="mt-2 text-xs text-[var(--ji-muted)]">
                  <strong className="text-[var(--ji-text)]">Note:</strong> Diagnostic fee is separate from repair costs and is not applied to the final repair cost.
                </div>
                <ul className="mt-6 space-y-2 text-sm">
                  {INCLUDED.map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <CheckCircle size={14} weight="duotone" className="text-[var(--ji-accent)] mt-1 flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 text-xs text-[var(--ji-muted)] inline-flex items-start gap-2">
                  <Info size={14} weight="duotone" className="mt-0.5 flex-shrink-0" />
                  <span>All repair pricing is given upfront — before any work begins.</span>
                </div>
              </>
            )}
          </motion.div>

          <div className="ji-card p-6" style={{ borderRadius: 4 }}>
            <div className="ji-overline mb-3">Prefer to talk first?</div>
            <a href={`tel:${BUSINESS.primaryPhone.tel}`} data-testid="diagnostic-call-cta" className="ji-btn-outline w-full inline-flex items-center justify-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
              Call {BUSINESS.primaryPhone.display}
            </a>
          </div>
        </aside>
      </section>
    </div>
  );
}
